import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ModalDialog from '../ModalDialog';
import { Box, CircularProgress, Grid, IconButton, TextField, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useAuth } from '../../../hooks/useAuth';
import { CancelPresentationRounded, Close, FileOpen } from '@mui/icons-material';
import Files from 'react-files';
import AutocompleteComponent from '../Autocomplete';
import { PAGES_DATA, USER_BUG_FILES_UPLOAD_SIZE } from '../../../utils/constants';
import styles from './styles';
import { addBugReport } from '../../../store/actions/authorization';
import { enqueueSnackbar } from '../../../store/actions/snackbar';
import { listUserBugs } from '../../../store/actions/userBugs';

const BugReportDialog = ({
  isOpen,
  onCloseClick
}) => {
  const { user } = useAuth();
  const classes = styles();
  const dispatch = useDispatch();
  const [pagesByUserAccess] = useState(PAGES_DATA.filter((page) => page.label && page.roles.includes(user.type)).map(item => item?.label));
  const [files, setFiles] = useState([]);
  const [values, setValues] = useState({
    issue: '',
    description: '',
    location: []
  });
  const [loading, setLoading] = useState(false);

  const onDialogCloseClick = () => {
    onCloseClick();
  };

  const handleInputChange = (field, value) => {
    setValues((prevValues) => ({
      ...prevValues,
      [field]: value
    }));
  };

  const getModalTitle = () => {
    return (
            <>
                <Grid container alignItems="center" justifyContent="space-between">
                    <Grid item>
                        {'Bug Report'}
                    </Grid>
                    <Grid item>
                        <IconButton
                            onClick={onCloseClick}
                        >
                            <CancelPresentationRounded fontSize='large' />
                        </IconButton>
                    </Grid>
                </Grid>
            </>
    );
  };

  const handleUploadFiles = () => {
    if (!values.location.length) {
      dispatch(
        enqueueSnackbar(
          'Please select the location where the issue occurred.',
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return;
    }
    if (!values.issue) {
      dispatch(
        enqueueSnackbar(
          'Please mention the issue.',
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return;
    }
    if (!values.description) {
      dispatch(
        enqueueSnackbar(
          'Please describe the issue and steps to reproduce.',
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return;
    }
    setLoading(true);
    const requestBody = {
      query: {
        issue: values.issue,
        description: values?.description,
        location: values.location
      },
      user: {
        type: user?.type,
        companyId: user?.companyId,
        userId: user?.userId,
        unitSystem: user?.preferences?.unitSystem
      }
    };
    const formData = new FormData();
    Array.from(files).forEach((file) => {
      formData.append('files', file);
    });
    formData.append('payload', JSON.stringify(requestBody));
    dispatch(addBugReport(formData, () => {
      setLoading(false);
      onCloseClick();
      dispatch(listUserBugs());
    }));
  };

  const createDialogButtons = () => [
    {
      text: 'Submit',
      placeholder: 'Submit',
      'aria-label': 'Submit',
      onClick: handleUploadFiles,
      disabled: loading
    },
  ];

  const handleChange = (newFiles) => {
    const validFiles = [];

    newFiles.forEach((file) => {
      const fileType = file.type.split('/')[0];

      let maxSize;
      if (fileType === 'image') {
        maxSize = USER_BUG_FILES_UPLOAD_SIZE.image;
      } else if (fileType === 'video') {
        maxSize = USER_BUG_FILES_UPLOAD_SIZE.video;
      } else {
        maxSize = USER_BUG_FILES_UPLOAD_SIZE.other;
      }

      if (file.size <= maxSize) {
        validFiles.push(file);
      } else {
        dispatch(
          enqueueSnackbar(
           `The file "${file.name}" exceeds the size limit for ${fileType}s.`,
           'error',
           new Date().getTime() + Math.random()
          )
        );
      }
    });

    setFiles(prevFiles => [...prevFiles, ...validFiles]);
  };

  const handleFileRemove = (fileId) => {
    setFiles(prevFiles => prevFiles.filter(prevFile => prevFile.id !== fileId));
  };

  return (
        <ModalDialog
            title={getModalTitle()}
            isOpen={isOpen}
            onClose={onDialogCloseClick}
            buttons={createDialogButtons()}
            maxWidth={'sm'}
        >
            {loading
              ? (
                <Grid className={classes.uploadingBugReportLoader}>
                    <Typography variant='h5'>Please wait.. Submitting your bug report.</Typography>
                    <CircularProgress />
                </Grid>
                )
              : (
                <Grid sx={{ padding: 4 }}>
                    <AutocompleteComponent
                        title='Location'
                        data={values.location}
                        onChange={(event, values) => handleInputChange('location', values)}
                        options={pagesByUserAccess}
                    />
                    <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontWeight: 'bold', fontSize: '15px' }} gutterBottom>
                            Issue
                        </Typography>
                        <TextField
                            fullWidth
                            value={values.issue}
                            onChange={(e) => handleInputChange('issue', e.target.value)}
                        />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontWeight: 'bold', fontSize: '15px' }} gutterBottom>
                            Description
                        </Typography>
                        <TextField
                            multiline
                            fullWidth
                            minRows={2}
                            value={values.description}
                            placeholder='Describe the issue and steps to reproduce'
                            onChange={(e) => handleInputChange('description', e.target.value)}
                        />
                    </Box>
                    <Box className="files">
                      <Typography sx={{ fontWeight: 'bold', fontSize: '15px' }} gutterBottom>
                            Attachments
                        </Typography>
                        <Files
                            className={classes.fileSelectionArea}
                            onChange={handleChange}
                            multiple
                            clickable>
                            <Box textAlign='center'>
                                <Typography sx={{ textAlign: 'center', fontSize: '16px', mb: 2 }}>Please attach any pictures, videos or csv that are needed to show or reproduce the issue</Typography>
                                <FileOpen />
                            </Box>
                        </Files>
                    <Typography fontSize='14px'>
                      <span style={{ fontWeight: 'bold' }}>Max file sizes:</span> Images: 3MB, Videos: 75MB, Other files: 30MB
                      <br />
                    </Typography>
                    </Box>
                    <Box>
                        {files.length > 0 && (
                            <Box className={classes.filesList}>
                                {files.map((file, i) => (
                                    <Box key={`${file.id}key${i}`} className={classes.filesListItem}>
                                        <Box className={classes.filesListItemPreview}>
                                            {file.preview.type === 'image'
                                              ? <img className={classes.filesListItemPreviewImage} src={file.preview.url} />
                                              : <Box className={classes.filesListItemPreviewExtension}>{file.extension}</Box>}
                                        </Box>
                                        <Box className={classes.filesListItemContent}>
                                            <Box className={`${classes.filesListItemContentItem} ${classes.filesListItemContentItem1}`}>{file.name}</Box>
                                            <Box className={`${classes.filesListItemContentItem} ${classes.filesListItemContentItem2}`}>{file.sizeReadable}</Box>
                                        </Box>
                                        <IconButton
                                            className={classes.filesListItemRemove}
                                            onClick={() => handleFileRemove(file.id)}
                                        >
                                            <Close />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                </Grid>
                )}
        </ModalDialog>
  );
};

BugReportDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onCloseClick: PropTypes.func.isRequired,
};

export default BugReportDialog;
